import React from 'react'
import { LogOut, User, Lock, Key } from 'lucide-react'
import { Button } from './button'
import { supabase } from '../../lib/supabase'
import { toast } from 'sonner'
import { useNavigate } from 'react-router-dom'
import { trackEvent, EventCategory } from '../../lib/analytics'
import { Modal } from './modal'
import { useAuth } from '../../hooks/use-auth'
import { debug } from '../../lib/debug'
import { DEBUG } from '../../lib/constants'
import { PinManagementModal } from '../admin/pin-management-modal'

export function UserMenu() {
  const navigate = useNavigate()
  const [showModal, setShowModal] = React.useState(false)
  const [showPasswordModal, setShowPasswordModal] = React.useState(false)
  const [showPinModal, setShowPinModal] = React.useState(false)
  const [isLoggingOut, setIsLoggingOut] = React.useState(false)
  const menuRef = React.useRef<HTMLDivElement>(null)
  const { isAdmin } = useAuth()
  const prevStateRef = React.useRef({ showModal, showPasswordModal, showPinModal, isLoggingOut, isAdmin })

  // Only log state changes when they actually occur
  React.useEffect(() => {
    const prevState = prevStateRef.current
    const currentState = { showModal, showPasswordModal, showPinModal, isLoggingOut, isAdmin }
    
    if (JSON.stringify(prevState) !== JSON.stringify(currentState)) {
      debug(DEBUG.STATE, 'UserMenu: State changed', {
        from: prevState,
        to: currentState
      })
      prevStateRef.current = currentState
    }
  }, [showModal, showPasswordModal, showPinModal, isLoggingOut, isAdmin])

  // Close menu when clicking outside
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowModal(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = async () => {
    if (isLoggingOut) return
    
    setIsLoggingOut(true)
    try {
      const storageKeys = Object.keys(localStorage)
      const authKeys = storageKeys.filter(key => key.startsWith('sb-'))
      authKeys.forEach(key => localStorage.removeItem(key))

      await supabase.auth.signOut({ scope: 'local' })

      trackEvent({
        action: 'logout',
        category: EventCategory.Auth,
        label: 'user_logout'
      })

      setShowModal(false)
      setShowPasswordModal(false)
      setShowPinModal(false)
      
      toast.success('Successfully logged out')
      
      setTimeout(() => {
        navigate('/')
      }, 100)
    } catch (error) {
      console.error('Error logging out:', error)
      toast.error('Failed to log out. Please try again.')
    } finally {
      setIsLoggingOut(false)
    }
  }

  const handleChangePassword = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user?.email) {
        throw new Error('No user email found')
      }

      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/auth/update-password`,
      })

      if (error) throw error

      trackEvent({
        action: 'request',
        category: EventCategory.Auth,
        label: 'password_reset_requested'
      })

      toast.success('Password reset email sent. Please check your inbox.')
      setShowPasswordModal(false)
      setShowModal(false)
    } catch (error) {
      console.error('Error requesting password reset:', error)
      toast.error('Failed to request password reset')
    }
  }

  const handleManagePins = () => {
    trackEvent({
      action: 'click',
      category: EventCategory.Admin,
      label: 'manage_pins'
    })
    setShowPinModal(true)
    setShowModal(false)
  }

  return (
    <>
      <div className="relative" ref={menuRef}>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setShowModal(!showModal)}
          className="relative h-8 w-8"
        >
          <User className="h-5 w-5" />
        </Button>

        {showModal && (
          <div className="absolute right-0 mt-2 w-48 rounded-md bg-card shadow-lg border border-border">
            <div className="py-1">
              <button
                onClick={() => {
                  setShowPasswordModal(true)
                  setShowModal(false)
                }}
                className="flex w-full items-center px-4 py-2 text-sm hover:bg-muted/50 gap-2"
              >
                <Lock className="h-4 w-4" />
                Change Password
              </button>
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="flex w-full items-center px-4 py-2 text-sm hover:bg-muted/50 gap-2 text-destructive disabled:opacity-50"
              >
                <LogOut className="h-4 w-4" />
                {isLoggingOut ? 'Signing out...' : 'Sign Out'}
              </button>
            </div>
          </div>
        )}
      </div>

      <Modal
        open={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        title="Change Password"
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            To change your password, we'll send you an email with a password reset link. Click the link in the email to set your new password.
          </p>
          
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPasswordModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleChangePassword}
            >
              Send Reset Email
            </Button>
          </div>
        </div>
      </Modal>

    </>
  )
}